import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

// See api/DTOs/FieldSeasonDto.cs (FieldSeasonListItemDto).
export interface FieldSeasonItem {
  id: string;
  businessName: string;
  farmName?: string;
  fieldName: string;
  timePeriodName: string;
  timePeriodStartDate: string; // yyyy-mm-dd
  timePeriodEndDate: string;
  regionName?: string;
  // The season value, then any change part way through — see
  // api/Models/FieldSeasonIrrigationChange.cs.
  irrigationTypeId?: string;
  irrigationTypeName?: string;
  irrigationChanges: { id: string; effectiveDate: string; irrigationTypeId: string; irrigationTypeName: string }[];
  varietyName?: string;
  geneStackName?: string;
  areaHectares?: number;
  // The field's planting records that season, largest first. With more
  // than one (e.g. a refuge), varietyName/geneStackName are the largest
  // planting's and areaHectares the total — see api/Services/FieldSeasonPlantings.cs.
  plantings?: { recordId: string; varietyName: string; geneStackName?: string; areaHectares?: number }[];
  yieldBalesPerHa?: number;
  numberOfIrrigations?: number;
  totalWaterUseMlPerHa?: number;
  irrigationMethodName?: string;
  energySourceName?: string;
  waterSourceName?: string;
  previousCropName?: string;
  fallowLengthMonths?: number;
  distanceToGinKm?: number;
  attributes: Record<string, string>;
}

interface Props {
  items: FieldSeasonItem[];
  totalCount: number;
  page: number; // 0-indexed, as MUI TablePagination
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  showBusiness: boolean;
  // Admin only — archives the row (DELETE api/field-seasons/{id}).
  onArchive?: (item: FieldSeasonItem) => void;
  // Admin only — opens FieldSeasonIrrigationDialog.
  onEditIrrigation?: (item: FieldSeasonItem) => void;
}

const show = (value: string | number | null | undefined) => (value === null || value === undefined || value === '' ? '—' : value);

const formatDate = (isoDate: string) => isoDate.split('-').reverse().join('/');

// One row per field per season — the same flat shape as CCA's Supplementary
// Data sheet. "Other values" lists columns with no home of their own, and
// any value that didn't match its list. Shared by the admin Field Seasons
// screen and a business's Other Records tab.
const FieldSeasonTable: React.FC<Props> = ({ items, totalCount, page, pageSize, onPageChange, onPageSizeChange, showBusiness, onArchive, onEditIrrigation }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table
      size="small"
      sx={{
        '& .MuiTableCell-root': { py: 0.25, fontSize: '0.8rem', lineHeight: 1.35 },
        '& .MuiTableCell-head': { fontWeight: 600, whiteSpace: 'nowrap' },
        '& .MuiIconButton-root': { p: 0.25 },
      }}
    >
      <TableHead>
        <TableRow>
          {showBusiness && <TableCell>Business</TableCell>}
          <TableCell>Farm</TableCell>
          <TableCell>Field</TableCell>
          <TableCell>Season</TableCell>
          <TableCell>Region</TableCell>
          <TableCell>Irrigated / Dryland</TableCell>
          <TableCell>Variety</TableCell>
          <TableCell>Gene Stack</TableCell>
          <TableCell align="right">Area (ha)</TableCell>
          <TableCell align="right">Yield (bales/ha)</TableCell>
          <TableCell align="right">Irrigations</TableCell>
          <TableCell align="right">Water (ML/ha)</TableCell>
          <TableCell>Irrigation Method</TableCell>
          <TableCell>Energy Source</TableCell>
          <TableCell>Water Source</TableCell>
          <TableCell>Previous Crop</TableCell>
          <TableCell align="right">Fallow (months)</TableCell>
          <TableCell align="right">To Gin (km)</TableCell>
          <TableCell>Other values</TableCell>
          {onArchive && <TableCell align="right" />}
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.id} hover>
            {showBusiness && <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.businessName}</TableCell>}
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{show(item.farmName)}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.fieldName}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.timePeriodName}</TableCell>
            <TableCell>{show(item.regionName)}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Box display="flex" alignItems="flex-start" gap={0.5}>
                <Box>
                  {show(item.irrigationTypeName)}
                  {item.irrigationChanges.map((c) => (
                    <Box key={c.id} component="div" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {c.irrigationTypeName} from {formatDate(c.effectiveDate)}
                    </Box>
                  ))}
                </Box>
                {onEditIrrigation && (
                  <Tooltip title="Edit Irrigated / Dryland, and changes during the season">
                    <IconButton size="small" onClick={() => onEditIrrigation(item)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
            {item.plantings && item.plantings.length > 1 ? (
              // One line per planting, so variety, gene stack and hectares line up.
              <>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.plantings.map((p) => <Box key={p.recordId}>{p.varietyName}</Box>)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.plantings.map((p) => <Box key={p.recordId}>{show(p.geneStackName)}</Box>)}</TableCell>
                <TableCell align="right">
                  {item.plantings.map((p) => <Box key={p.recordId}>{show(p.areaHectares)}</Box>)}
                  <Tooltip title="The field season's area: the plantings' total">
                    <Box sx={{ borderTop: 1, borderColor: 'divider', fontWeight: 600 }}>{show(item.areaHectares)}</Box>
                  </Tooltip>
                </TableCell>
              </>
            ) : (
              <>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{show(item.varietyName)}</TableCell>
                <TableCell>{show(item.geneStackName)}</TableCell>
                <TableCell align="right">{show(item.areaHectares)}</TableCell>
              </>
            )}
            <TableCell align="right">{show(item.yieldBalesPerHa)}</TableCell>
            <TableCell align="right">{show(item.numberOfIrrigations)}</TableCell>
            <TableCell align="right">{show(item.totalWaterUseMlPerHa)}</TableCell>
            <TableCell>{show(item.irrigationMethodName)}</TableCell>
            <TableCell>{show(item.energySourceName)}</TableCell>
            <TableCell>{show(item.waterSourceName)}</TableCell>
            <TableCell>{show(item.previousCropName)}</TableCell>
            <TableCell align="right">{show(item.fallowLengthMonths)}</TableCell>
            <TableCell align="right">{show(item.distanceToGinKm)}</TableCell>
            <TableCell>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {Object.entries(item.attributes).map(([key, value]) => (
                  <Chip key={key} size="small" variant="outlined" label={`${key}: ${value}`} />
                ))}
              </Box>
            </TableCell>
            {onArchive && (
              <TableCell align="right">
                <Tooltip title="Archive">
                  <IconButton size="small" color="error" onClick={() => onArchive(item)}><DeleteIcon fontSize="small" /></IconButton>
                </Tooltip>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
    <TablePagination
      component="div"
      count={totalCount}
      page={page}
      onPageChange={(_, newPage) => onPageChange(newPage)}
      rowsPerPage={pageSize}
      onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={[25, 50, 100]}
    />
  </TableContainer>
);

export default FieldSeasonTable;
